/**
 * PricingSection - Fair & Transparent Pricing display
 * Strategic percentage-based pricing model
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Globe,
  Send,
  Store,
  ArrowRight,
  Check,
  Sparkles
} from 'lucide-react';

const PricingSection = () => {
  const { t } = useTranslation();
  const pricingPlans = [
    {
      id: 'global-fx',
      name: t('home.pricing.fxName'),
      priceHighlight: '2.5%',
      pricePrefix: '',
      priceSuffix: t('home.pricing.fxSuffix'),
      icon: Globe,
      color: 'emerald',
      features: [
        t('home.pricing.fxF1'),
        t('home.pricing.fxF2'),
        t('home.pricing.fxF3'),
        t('home.pricing.fxF4')
      ],
      popular: false
    },
    {
      id: 'transfers',
      name: t('home.pricing.trName'),
      priceHighlight: '1.5%',
      pricePrefix: '',
      priceSuffix: t('home.pricing.trSuffix'),
      icon: Send,
      color: 'blue',
      features: [
        t('home.pricing.trF1'),
        t('home.pricing.trF2'),
        t('home.pricing.trF3'),
        t('home.pricing.trF4')
      ],
      popular: true
    },
    {
      id: 'merchant',
      name: t('home.pricing.mrName'),
      priceHighlight: '1.5%',
      pricePrefix: '',
      priceSuffix: t('home.pricing.mrSuffix'),
      icon: Store,
      color: 'violet',
      features: [
        t('home.pricing.mrF1'),
        t('home.pricing.mrF2'),
        t('home.pricing.mrF3'),
        t('home.pricing.mrF4')
      ],
      popular: false
    }
  ];

  const getColorClasses = (color, type) => {
    const colors = {
      emerald: {
        bg: 'bg-emerald-50 dark:bg-emerald-900/20',
        border: 'border-emerald-200 dark:border-emerald-800',
        icon: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400',
        price: 'text-emerald-600 dark:text-emerald-400',
        check: 'text-emerald-500'
      },
      blue: {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        border: 'border-blue-200 dark:border-blue-800',
        icon: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400',
        price: 'text-blue-600 dark:text-blue-400',
        check: 'text-blue-500'
      },
      violet: {
        bg: 'bg-violet-50 dark:bg-violet-900/20',
        border: 'border-violet-200 dark:border-violet-800',
        icon: 'bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400',
        price: 'text-violet-600 dark:text-violet-400',
        check: 'text-violet-500'
      }
    };
    return colors[color]?.[type] || '';
  };

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-full text-emerald-700 dark:text-emerald-300 text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            {t('home.pricing.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
            {t('home.pricing.title')}
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            {t('home.pricing.subtitle')}
          </p>
        </div>

        {/* Pricing Graphic */}
        <div className="flex justify-center mb-12">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b0033424-6b4b-45d2-80cb-8129b7f18625/images/69ba2ecd5fcf2e54292866db948e188f7916d82a7e92a1830d8cfbf7766679a1.png"
            alt="ParachPay transparent pricing illustration"
            className="w-full max-w-2xl h-auto object-contain rounded-2xl"
          />
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-6">
          {pricingPlans.map((plan) => {
            const IconComponent = plan.icon;
            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl border-2 ${getColorClasses(plan.color, 'border')} ${getColorClasses(plan.color, 'bg')} p-6 lg:p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${plan.popular ? 'ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-slate-900' : ''}`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full shadow-lg">
                      {t('home.pricing.popular')}
                    </span>
                  </div>
                )}

                {/* Icon */}
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl ${getColorClasses(plan.color, 'icon')} mb-5`}>
                  <IconComponent className="w-7 h-7" />
                </div>

                {/* Plan Name */}
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                  {plan.name}
                </h3>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-1 flex-wrap">
                    {plan.pricePrefix && (
                      <span className="text-slate-600 dark:text-slate-400 text-sm">
                        {plan.pricePrefix}
                      </span>
                    )}
                    <span className={`text-4xl lg:text-5xl font-extrabold ${getColorClasses(plan.color, 'price')}`}>
                      {plan.priceHighlight}
                    </span>
                    {plan.priceSuffix && (
                      <span className="text-slate-500 dark:text-slate-400 text-sm ml-1">
                        {plan.priceSuffix}
                      </span>
                    )}
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${getColorClasses(plan.color, 'check')}`} />
                      <span className="text-slate-700 dark:text-slate-300 text-sm">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Footnote */}
        <p className="text-center text-xs text-slate-400 dark:text-slate-500 mb-8">
          {t('home.pricing.footnote')}
        </p>

        {/* CTA Button */}
        <div className="text-center">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-lg group"
          >
            {t('home.pricing.cta')}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            {t('home.pricing.ctaNote')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
